import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, retry, shareReplay, takeUntil } from 'rxjs/operators';

import { TutorialResponseModel } from '../models/tutorial-response.model';

const API_ENDPOINT = '/api/tutorial/';

@Injectable({
  providedIn: 'root',
})
export class TutorialService {

  public tutorialToggleForPage: Subject<boolean>;
  public tutorialEnabled: Subject<boolean>;

  private assistantAndOverviewTutorialResponse: Observable<TutorialResponseModel>;

  /**
   * Constructor
   *
   * @param http HttpClient
   */
  public constructor(private http: HttpClient) {
    this.tutorialToggleForPage = new Subject<boolean>();
    this.tutorialEnabled = new Subject<boolean>();
  }

  /**
   * Expose the tutorial close observable.
   * @returns {Observable}
   */
  public getTutorialToggle(): Observable<boolean> {
    return this.tutorialToggleForPage.asObservable();
  }

  /**
   * Get the tutorial overview data
   *
   * @return Observable<DashboardWidgetModel[]>
   */
  public getAssistantAndOverviewTutorialResponse(): Observable<TutorialResponseModel> {
    if (!this.assistantAndOverviewTutorialResponse) {
      this.assistantAndOverviewTutorialResponse = this.loadAssistantAndOverviewTutorialResponse().pipe(
        shareReplay(1),
        takeUntil(this.tutorialEnabled),
      );
    }

    return this.assistantAndOverviewTutorialResponse;
  }

  /**
   * Loads the tutorial overview data
   *
   * @return Promise regarding getting all widgets.
   */
  public loadAssistantAndOverviewTutorialResponse(): Observable<TutorialResponseModel> {
    return this.http.get(API_ENDPOINT + 'get')
      .pipe(
        retry(3),
        map((response: {message: null|string, items: TutorialResponseModel}) => {
          return response.items;
        }),
      );
  }

  /**
   * Send Post Request that the player has seen the video
   *
   * @return string[]
   */
  public sendSeenTutorial(routeName: string, tutorialHash: string): Observable<boolean> {
    return this.http.post(API_ENDPOINT + 'seen/' + routeName + '/' + tutorialHash, {})
      .pipe(
        map((response: {items: {success: boolean}}) => {
          return response.items.success;
        }),
      );
  }

  /**
   * Disables the Tutorial, also forcing a reload.
   */
  public enableTutorial(enabled: boolean) {
    this.assistantAndOverviewTutorialResponse = null;
    this.tutorialEnabled.next(enabled);
  }
}
